import { useEffect, useState } from "react";
import { useAccount } from "../../controllers/useAccount";
import type { CompanyProfile, IndividualProfile } from "../../models/account";
import { SubscriptionView } from "./SubscriptionView";

const GENDER_LABEL: Record<string, string> = {
  MALE: "남성", FEMALE: "여성", OTHER: "기타", UNDISCLOSED: "밝히지 않음",
};
const COMPANY_SIZE_LABEL: Record<string, string> = {
  SOLO: "1인", SMALL: "2-49인", MEDIUM: "50-249인", LARGE: "250-999인",
  ENTERPRISE: "1000인 이상", UNKNOWN: "알 수 없음",
};

// The account/profile section: shared account fields, individual-vs-company profile forms (based
// on accountType), terms agreement, and marketing opt-in. Also hosts the subscription tab.
export function AccountView() {
  const acc = useAccount();
  const [tab, setTab] = useState<"profile" | "subscription">("profile");

  if (acc.loading && !acc.account) return <div className="center">불러오는 중…</div>;
  if (!acc.account) {
    return (
      <div className="detail">
        <p>{acc.error ?? "계정 정보를 불러오지 못했습니다."}</p>
      </div>
    );
  }

  return (
    <div className="detail">
      <div className="detail-name"><h1>내 계정</h1></div>
      <p className="meta">{acc.account.email}{acc.account.emailVerified ? "" : " · 이메일 미인증"}</p>

      <div className="tabs">
        <button className={`tab ${tab === "profile" ? "active" : ""}`} onClick={() => setTab("profile")}>프로필</button>
        <button className={`tab ${tab === "subscription" ? "active" : ""}`} onClick={() => setTab("subscription")}>구독</button>
      </div>

      {acc.error && <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}><b style={{ color: "var(--amber)" }}>오류:</b> {acc.error}</div>}

      {tab === "profile" ? <ProfileTab acc={acc} /> : <SubscriptionView />}
    </div>
  );
}

function ProfileTab({ acc }: { acc: ReturnType<typeof useAccount> }) {
  const account = acc.account!;
  const [displayName, setDisplayName] = useState(account.displayName ?? "");
  const [phone, setPhone] = useState(account.phone ?? "");
  const [locale, setLocale] = useState(account.locale ?? "");
  const [zoneId, setZoneId] = useState(account.zoneId ?? "");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDisplayName(account.displayName ?? "");
    setPhone(account.phone ?? "");
    setLocale(account.locale ?? "");
    setZoneId(account.zoneId ?? "");

  }, [account]);

  async function saveBasics() {
    setBusy(true); setSaved(false);
    try {
      await acc.saveAccount({ displayName, phone, locale, zoneId });
      setSaved(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="detail-body">
      <div className="detail-main">
        <div className="info-card" style={{ marginBottom: 16 }}>
          <h4>기본 정보</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 12 }}>
            <label className="sdui-field">
              <span className="sdui-label">표시 이름</span>
              <input className="sdui-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </label>
            <label className="sdui-field">
              <span className="sdui-label">전화번호</span>
              <input className="sdui-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </label>
            <div style={{ display: "flex", gap: 14 }}>
              <label className="sdui-field" style={{ flex: 1 }}>
                <span className="sdui-label">언어(locale)</span>
                <input className="sdui-input" placeholder="ko-KR" value={locale} onChange={(e) => setLocale(e.target.value)} />
              </label>
              <label className="sdui-field" style={{ flex: 1 }}>
                <span className="sdui-label">시간대</span>
                <input className="sdui-input" placeholder="Asia/Seoul" value={zoneId} onChange={(e) => setZoneId(e.target.value)} />
              </label>
            </div>
            <div className="sdui-help">
              마케팅 정보 수신: {account.marketingOptIn ? "동의" : "미동의"} (변경 API는 아직 제공되지 않습니다)
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className="btn primary" style={{ width: "auto" }} disabled={busy} onClick={saveBasics}>
                {busy ? "저장 중…" : "저장"}
              </button>
            </div>
            {saved && <div className="note"><b>저장됨</b></div>}
          </div>
        </div>

        {account.accountType === "INDIVIDUAL" ? (
          <IndividualProfileForm profile={account.individualProfile ?? {}} onSave={acc.saveIndividualProfile} />
        ) : (
          <CompanyProfileForm profile={account.companyProfile ?? {}} onSave={acc.saveCompanyProfile} />
        )}
      </div>

      <aside className="aside">
        <div className="info-card">
          <h4>계정 상태</h4>
          <div className="kv"><span>유형</span><span>{account.accountType === "INDIVIDUAL" ? "개인" : "기업"}</span></div>
          <div className="kv"><span>상태</span><span>{account.status}</span></div>
          <div className="kv"><span>약관 동의</span><span>{account.termsAgreedAt ? `동의됨 (${new Date(account.termsAgreedAt).toLocaleDateString()})` : "미동의"}</span></div>
        </div>
        {!account.termsAgreedAt && (
          <div className="note">
            <b>서비스 이용약관에 아직 동의하지 않았습니다.</b><br />
            <button className="btn primary" style={{ marginTop: 10, width: "auto" }} onClick={() => void acc.acceptTerms()}>약관에 동의</button>
          </div>
        )}
      </aside>
    </div>
  );
}

function IndividualProfileForm({
  profile, onSave,
}: { profile: IndividualProfile; onSave: (p: IndividualProfile) => Promise<void> }) {
  const [form, setForm] = useState<IndividualProfile>(profile);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(profile), [profile]);
  const set = <K extends keyof IndividualProfile>(k: K, v: IndividualProfile[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setBusy(true);
    try { await onSave(form); } finally { setBusy(false); }
  }

  return (
    <div className="info-card">
      <h4>개인 프로필</h4>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 12 }}>
        <div style={{ display: "flex", gap: 14 }}>
          <Field label="성"><input className="sdui-input" value={form.familyName ?? ""} onChange={(e) => set("familyName", e.target.value)} /></Field>
          <Field label="이름"><input className="sdui-input" value={form.givenName ?? ""} onChange={(e) => set("givenName", e.target.value)} /></Field>
        </div>
        <Field label="닉네임"><input className="sdui-input" value={form.nickname ?? ""} onChange={(e) => set("nickname", e.target.value)} /></Field>
        <Field label="성별">
          <select className="sdui-input" value={form.gender ?? "UNDISCLOSED"} onChange={(e) => set("gender", e.target.value as IndividualProfile["gender"])}>
            {Object.entries(GENDER_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="생년월일"><input className="sdui-input" type="date" value={form.birthDate ?? ""} onChange={(e) => set("birthDate", e.target.value)} /></Field>
        <div style={{ display: "flex", gap: 14 }}>
          <Field label="직함"><input className="sdui-input" value={form.jobTitle ?? ""} onChange={(e) => set("jobTitle", e.target.value)} /></Field>
          <Field label="소속"><input className="sdui-input" value={form.organization ?? ""} onChange={(e) => set("organization", e.target.value)} /></Field>
        </div>
        <Field label="자기소개"><textarea className="sdui-input" rows={3} value={form.bio ?? ""} onChange={(e) => set("bio", e.target.value)} /></Field>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="btn primary" style={{ width: "auto" }} disabled={busy} onClick={save}>{busy ? "저장 중…" : "프로필 저장"}</button>
        </div>
      </div>
    </div>
  );
}

function CompanyProfileForm({
  profile, onSave,
}: { profile: CompanyProfile; onSave: (p: CompanyProfile) => Promise<void> }) {
  const [form, setForm] = useState<CompanyProfile>(profile);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(profile), [profile]);
  const set = <K extends keyof CompanyProfile>(k: K, v: CompanyProfile[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setBusy(true);
    try { await onSave(form); } finally { setBusy(false); }
  }

  return (
    <div className="info-card">
      <h4>기업 프로필</h4>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 12 }}>
        <div style={{ display: "flex", gap: 14 }}>
          <Field label="법인명"><input className="sdui-input" value={form.legalName ?? ""} onChange={(e) => set("legalName", e.target.value)} /></Field>
          <Field label="브랜드명"><input className="sdui-input" value={form.brandName ?? ""} onChange={(e) => set("brandName", e.target.value)} /></Field>
        </div>
        <div style={{ display: "flex", gap: 14 }}>
          <Field label="사업자등록번호"><input className="sdui-input" value={form.registrationNumber ?? ""} onChange={(e) => set("registrationNumber", e.target.value)} /></Field>
          <Field label="업종"><input className="sdui-input" value={form.industry ?? ""} onChange={(e) => set("industry", e.target.value)} /></Field>
        </div>
        <Field label="기업 규모">
          <select className="sdui-input" value={form.companySize ?? "UNKNOWN"} onChange={(e) => set("companySize", e.target.value as CompanyProfile["companySize"])}>
            {Object.entries(COMPANY_SIZE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <div style={{ display: "flex", gap: 14 }}>
          <Field label="담당자"><input className="sdui-input" value={form.contactName ?? ""} onChange={(e) => set("contactName", e.target.value)} /></Field>
          <Field label="담당자 이메일"><input className="sdui-input" value={form.contactEmail ?? ""} onChange={(e) => set("contactEmail", e.target.value)} /></Field>
        </div>
        <Field label="본사 주소"><input className="sdui-input" value={form.hqAddressLine1 ?? ""} onChange={(e) => set("hqAddressLine1", e.target.value)} /></Field>
        <Field label="회사 소개"><textarea className="sdui-input" rows={3} value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} /></Field>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="btn primary" style={{ width: "auto" }} disabled={busy} onClick={save}>{busy ? "저장 중…" : "프로필 저장"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="sdui-field" style={{ flex: 1 }}>
      <span className="sdui-label">{label}</span>
      {children}
    </label>
  );
}
